import {
  ActivityPacing,
  ActivityType as SocrativeActivityType,
} from '@showbie-socrative/socrative-utils';
import {
  ActivityError,
  ActivityStartedMessage,
  Activity as ActivityType,
  ActivityType as AssessmentActivityType,
} from '@showbie/utilities/message';
import { useAtom, useAtomValue } from 'jotai';
import snakecaseKeys from 'snakecase-keys';

import { translate } from '~l10n/client';
import { toast } from '~shared/components/Toast';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { useStartQuizMutation } from '~teacher/hook/activity';

import { sendMessageWithMessageOnly } from '../../showbie/messages/sendMessageHelper';
import {
  deliveryMethodAtom,
  isActivityStartingAtom,
  oneAttemptAtom,
  quizIdAtom,
  quizNameAtom,
  quizTimerSecondsAtom,
  quizTypeAtom,
  randomAnswersAtom,
  randomQuestionsAtom,
  requireNamesAtom,
  showFeedbackAtom,
  showResultsAtom,
  workspaceIdAtom,
  workspaceQuizIdAtom,
} from './atoms';
import { useLaunchRoute } from './context/LaunchModalContext';
import { useIsRoomRostered, useRoomId } from './context/RoomContext';
import { DeliveryMethod } from './DeliveryMethod';

const activity = require('../../../../../shared/models/activity');
const user = require('../../../../../shared/models/user');

type PacingStudentNav = {
  pacing: ActivityPacing;
  allowStudentNav: boolean;
};

const deliveryMethodToPacingMap = new Map<DeliveryMethod, PacingStudentNav>([
  [
    DeliveryMethod.InstantFeedback,
    {
      pacing: ActivityPacing.Student, // Student picks question
      allowStudentNav: false, // student goes 1 question at a time
    },
  ],
  [
    DeliveryMethod.OpenNavigation,
    {
      pacing: ActivityPacing.Student, // student picks question
      allowStudentNav: true, // Student can go from question to question
    },
  ],
  [
    DeliveryMethod.TeacherPaced,
    {
      pacing: ActivityPacing.Teacher, // Teacher controls question order
      allowStudentNav: false, // student has no navigation
    },
  ],
]);

export function useBeginQuiz() {
  const applicationContext = useTeacherApplicationContext();
  const roomId = useRoomId();
  const isRoomRostered = useIsRoomRostered();

  const [isStartingActivity, setIsStartingActivity] = useAtom(
    isActivityStartingAtom
  );

  const quizId = useAtomValue(quizIdAtom);
  const quizName = useAtomValue(quizNameAtom);
  const workspaceId = useAtomValue(workspaceIdAtom);
  const workspaceQuizId = useAtomValue(workspaceQuizIdAtom);
  const showResults = useAtomValue(showResultsAtom);
  const oneAttempt = useAtomValue(oneAttemptAtom);
  const deliveryMethod = useAtomValue(deliveryMethodAtom);
  const showFeedback = useAtomValue(showFeedbackAtom);
  const requireNames = useAtomValue(requireNamesAtom);
  const randomAnswers = useAtomValue(randomAnswersAtom);
  const randomQuestions = useAtomValue(randomQuestionsAtom);
  const quizTimerSeconds = useAtomValue(quizTimerSecondsAtom);
  const quizType = useAtomValue(quizTypeAtom);

  const { pacing, allowStudentNav } =
    deliveryMethodToPacingMap.get(deliveryMethod);

  const launchRoute = useLaunchRoute();

  const startQuizMutation = useStartQuizMutation();

  const handleStartQuiz = () => {
    startQuizMutation.mutate(
      {
        quizId: quizId,
        quizName: quizName,
        workspaceId: workspaceId,
        workspaceQuizId: workspaceQuizId,
        roomId: roomId,
        roomHasRoster: isRoomRostered,
        launchRoute: launchRoute,
        quizType: quizType,
        isShowbieActivity:
          applicationContext === TeacherApplicationContext.ShowbieAssessments,
        settings: {
          pacing: pacing,
          allowStudentNav: allowStudentNav,
          requireNames: requireNames,
          showFeedback: showFeedback,
          showResults: showResults,
          oneAttempt: oneAttempt,
          randomAnswers: randomAnswers,
          randomQuestions: randomQuestions,
          quizTimerSeconds: quizTimerSeconds,
        },
      },
      {
        onError: (error) => {
          setIsStartingActivity(false);

          switch (applicationContext) {
            case TeacherApplicationContext.ShowbieAssessments: {
              const message: ActivityError = {
                type: ActivityType.Error,
                payload: {
                  code: 'LAUNCH_ERROR',
                  message: (error as any).message,
                },
              };

              sendMessageWithMessageOnly(message);
              break;
            }
            case TeacherApplicationContext.Socrative:
              toast.critical(
                translate('An error occurred while trying to launch')
              );
              break;
          }
        },
        onSuccess: (data) => {
          switch (applicationContext) {
            case TeacherApplicationContext.ShowbieAssessments: {
              const message: ActivityStartedMessage = {
                type: ActivityType.Started,
                payload: {
                  activity_type: socrativeActivityTypeToAssessmentType(
                    data.activityType
                  ),
                  activity_instance_id: data.id,
                  quiz_id: data.activityId,
                  quiz_name: quizName,
                },
              };

              sendMessageWithMessageOnly(message);
              break;
            }
            case TeacherApplicationContext.Socrative: {
              // Prep legacy activity model
              activity.prepareForNewActivity();
              activity.setSettings(snakecaseKeys(data), {});
              activity.set(
                { hideStudentResponses: false, hideStudentName: false },
                { silent: true }
              );

              // Launching in a rostered room enables hand raise by default
              // server-side (SOC-5767). Mirror it in the legacy user model before
              // navigating so the header toggle reflects it immediately — the
              // results view's header.render('results') reads getHandRaiseStatus().
              if (isRoomRostered) {
                user.setHandraiseStatus(true);
              }

              window.SOCRATIVE.teacherRouter.navigate(`results/live`, {
                trigger: true,
              });
              break;
            }
          }
        },
      }
    );
  };

  const beginQuiz = () => {
    setIsStartingActivity(true);

    switch (applicationContext) {
      case TeacherApplicationContext.ShowbieAssessments:
        handleStartQuiz();
        break;
      case TeacherApplicationContext.Socrative:
        handleStartQuiz();
    }
  };
  return { isStartingActivity, beginQuiz };
}

function socrativeActivityTypeToAssessmentType(
  activityType: SocrativeActivityType
) {
  switch (activityType) {
    case SocrativeActivityType.Quiz:
      return AssessmentActivityType.Quiz;
    case SocrativeActivityType.SpaceRace:
      return AssessmentActivityType.SpaceRace;
    case SocrativeActivityType.QuickQuestionShortAnswer:
      return AssessmentActivityType.QuickQuestion;
    default:
      return AssessmentActivityType.Quiz;
  }
}
